/**
 * Správce profilů — samostatná entita mimo běžné nastavení.
 * Seznam s řazením, viditelností a stylem; editor profilu umí přepsat
 * výchozí hodnoty parametrů (s jasným rozlišením zděděno × přepsáno).
 */
import { resolveParams, type ParamId, type ParamValue } from '../core/params';
import {
  createProfile,
  duplicateProfile,
  moveProfile,
  removeProfile,
  sortedProfiles,
  updateProfile,
  type Profile,
} from '../core/profiles';
import { applyProfileToSession, type Store } from '../state/store';
import { openDialog } from './dialogs';
import { el, clear } from './dom';
import { infoDot } from './help';
import { icon, iconOrEmoji, isIconName, type IconName } from './icons';
import { renderParamEditor } from './paramsUi';

/** Ikony nabízené v editoru profilu. */
const PROFILE_ICONS: IconName[] = [
  'profile', 'star', 'gauge', 'money', 'leaf', 'comfort', 'walk', 'bike', 'car', 'bus',
  'route', 'map-pin', 'clock', 'day', 'night', 'custom',
];

export function openProfileManager(store: Store, onChanged: () => void): void {
  const body = el('div', {});
  const listBox = el('div', { className: 'profile-list', role: 'list', 'aria-label': 'Profily nastavení' });

  const rerender = (): void => {
    clear(listBox);
    const s = store.get();
    for (const p of sortedProfiles(s.profiles)) {
      const isActive = p.id === s.activeProfileId;
      listBox.append(
        el(
          'div',
          { className: `profile-row${isActive ? ' profile-row--active' : ''}`, role: 'listitem', style: `--p-color:${p.color}` },
          el('span', { className: 'profile-row__dot', 'aria-hidden': 'true' }),
          el('span', { className: 'profile-row__icon', 'aria-hidden': 'true' }, iconOrEmoji(p.icon)),
          el(
            'div',
            { className: 'profile-row__main' },
            el('div', { className: 'profile-row__name' }, p.name, isActive ? el('span', { className: 'badge badge--rec', style: 'margin-left:6px' }, 'aktivní') : null),
            p.description ? el('div', { className: 'profile-row__desc' }, p.description) : null,
            overridesSummary(p),
          ),
          el(
            'div',
            { className: 'profile-row__actions' },
            !isActive
              ? el(
                  'button',
                  {
                    className: 'btn btn--small',
                    onclick: () => {
                      store.update(applyProfileToSession(store.get(), p.id));
                      onChanged();
                      rerender();
                    },
                  },
                  'Aktivovat',
                )
              : null,
            iconBtn('chevron-up', `Posunout ${p.name} výš`, () => {
              store.update({ profiles: moveProfile(store.get().profiles, p.id, -1) });
              rerender();
            }),
            iconBtn('chevron-down', `Posunout ${p.name} níž`, () => {
              store.update({ profiles: moveProfile(store.get().profiles, p.id, 1) });
              rerender();
            }),
            iconBtn(p.visible ? 'eye' : 'eye-off', p.visible ? `Skrýt ${p.name} z přepínače` : `Zobrazit ${p.name} v přepínači`, () => {
              store.update({ profiles: updateProfile(store.get().profiles, p.id, { visible: !p.visible }) });
              onChanged();
              rerender();
            }),
            iconBtn('edit', `Upravit profil ${p.name}`, () => openProfileEditor(store, p.id, () => { onChanged(); rerender(); })),
            iconBtn('plus', `Duplikovat profil ${p.name}`, () => {
              store.update({ profiles: [...store.get().profiles, duplicateProfile(store.get().profiles, p)] });
              rerender();
            }),
            store.get().profiles.length > 1
              ? iconBtn('trash', `Smazat profil ${p.name}`, () => {
                  if (!window.confirm(`Smazat profil „${p.name}"?`)) return;
                  const next = removeProfile(store.get().profiles, p.id);
                  const patch: Record<string, unknown> = { profiles: next };
                  if (store.get().activeProfileId === p.id) patch.activeProfileId = sortedProfiles(next)[0].id;
                  store.update(patch as never);
                  onChanged();
                  rerender();
                })
              : null,
          ),
        ),
      );
    }
  };

  body.append(
    el(
      'div',
      { className: 'muted', style: 'margin-bottom:8px' },
      'Profil je sada vašich preferencí (váhy, řazení, špička, zóny…). Přepnutím profilu se přeladí celá aplikace. ',
    ),
    listBox,
    el(
      'div',
      { style: 'margin-top:10px' },
      el(
        'button',
        {
          className: 'btn',
          onclick: () => {
            const p = createProfile(store.get().profiles);
            store.update({ profiles: [...store.get().profiles, p] });
            openProfileEditor(store, p.id, () => { onChanged(); rerender(); });
            rerender();
          },
        },
        icon('plus'),
        ' Nový profil',
      ),
    ),
  );

  rerender();
  const dlg = openDialog({
    title: 'Profily',
    body,
    wide: true,
    footer: [el('button', { className: 'btn btn--primary', onclick: () => dlg.close() }, 'Hotovo')],
  });

  function iconBtn(name: IconName, label: string, onclick: () => void): HTMLElement {
    return el('button', { className: 'btn btn--small btn--icon btn--ghost', 'aria-label': label, title: label, onclick }, icon(name));
  }
}

function overridesSummary(p: Profile): HTMLElement | null {
  const n = Object.keys(p.overrides).length;
  if (n === 0) return el('div', { className: 'profile-row__desc muted' }, 'Bez vlastních přepisů — vše zděděné.');
  return el('div', { className: 'profile-row__desc muted' }, `${n}× vlastní hodnota parametru` + (p.inheritGlobal ? '' : ' · nedědí globální nastavení'));
}

/* ------------------------------------------------------------------ */
/* Editor profilu                                                       */
/* ------------------------------------------------------------------ */

export function openProfileEditor(store: Store, profileId: string, onChanged: () => void): void {
  const profile = (): Profile | undefined => store.get().profiles.find((p) => p.id === profileId);
  const p0 = profile();
  if (!p0) return;

  const nameInput = el('input', { className: 'input', type: 'text', value: p0.name, 'aria-label': 'Název profilu' }) as HTMLInputElement;
  const descInput = el('input', { className: 'input', type: 'text', value: p0.description, placeholder: 'Krátký popis (volitelné)', 'aria-label': 'Popis profilu' }) as HTMLInputElement;
  const colorInput = el('input', { type: 'color', value: /^#[0-9a-f]{6}$/i.test(p0.color) ? p0.color : '#3b82f6', className: 'input', style: 'height:36px;padding:3px;width:64px', 'aria-label': 'Barva profilu' }) as HTMLInputElement;
  const visibleCheck = el('input', { type: 'checkbox', checked: p0.visible }) as HTMLInputElement;
  const inheritCheck = el('input', { type: 'checkbox', checked: p0.inheritGlobal }) as HTMLInputElement;

  let chosenIcon = p0.icon;
  const iconGrid = el('div', { className: 'icon-grid', role: 'radiogroup', 'aria-label': 'Ikona profilu' });
  const renderIcons = (): void => {
    clear(iconGrid);
    for (const name of PROFILE_ICONS) {
      iconGrid.append(
        el(
          'button',
          {
            className: `icon-pick${chosenIcon === name ? ' icon-pick--on' : ''}`,
            type: 'button',
            role: 'radio',
            'aria-checked': String(chosenIcon === name),
            'aria-label': `Ikona ${name}`,
            onclick: () => {
              chosenIcon = name;
              renderIcons();
            },
          },
          icon(name, { size: 18 }),
        ),
      );
    }
  };
  renderIcons();

  const saveMeta = (): void => {
    store.update({
      profiles: updateProfile(store.get().profiles, profileId, {
        name: nameInput.value.trim() || p0.name,
        description: descInput.value.trim(),
        color: colorInput.value,
        icon: chosenIcon,
        visible: visibleCheck.checked,
        inheritGlobal: inheritCheck.checked,
      }),
    });
    onChanged();
  };

  // Editor přepisů parametrů profilu: efektivní hodnota = profil > (globál) > výchozí.
  const paramsBox = el('div', {});
  const renderParams = (): void => {
    clear(paramsBox);
    const cur = profile();
    if (!cur) return;
    paramsBox.append(
      renderParamEditor({
        store,
        hideSensitive: true,
        inheritedLabel: cur.inheritGlobal ? 'zděděno' : 'výchozí',
        valueOf: (id: ParamId): ParamValue =>
          resolveParams(store.get().paramValues, profile() ?? null).get(id),
        isOverridden: (id: ParamId) => profile()?.overrides[id] !== undefined,
        set: (id: ParamId, value: ParamValue) => {
          const cur2 = profile();
          if (!cur2) return;
          store.update({
            profiles: updateProfile(store.get().profiles, profileId, {
              overrides: { ...cur2.overrides, [id]: value },
            }),
          });
          afterParamChange();
        },
        reset: (id: ParamId) => {
          const cur2 = profile();
          if (!cur2) return;
          const ov = { ...cur2.overrides };
          delete ov[id];
          store.update({ profiles: updateProfile(store.get().profiles, profileId, { overrides: ov }) });
          afterParamChange();
        },
      }),
    );
  };
  const afterParamChange = (): void => {
    // Když edituji aktivní profil, promítnu změny hned do sezení.
    if (store.get().activeProfileId === profileId) {
      store.update(applyProfileToSession(store.get(), profileId));
    }
    onChanged();
  };
  renderParams();

  const body = el(
    'div',
    {},
    el(
      'div',
      { className: 'field-grid' },
      field('Název', nameInput),
      field('Popis', descInput),
      field('Barva', colorInput),
    ),
    el('div', { className: 'label', style: 'margin-top:8px' }, 'Ikona'),
    iconGrid,
    el(
      'div',
      { className: 'row row--wrap', style: 'margin-top:8px' },
      el('label', { className: 'switch' }, visibleCheck, el('span', {}, 'Zobrazit v přepínači profilů')),
      el(
        'label',
        { className: 'switch' },
        inheritCheck,
        el('span', {}, 'Dědit globální nastavení'),
      ),
      infoDot(
        store,
        'Zapnuto: co profil nepřepíše, převezme z globálního nastavení. Vypnuto: profil staví jen na výchozích hodnotách aplikace.',
      ),
    ),
    el(
      'div',
      { className: 'card__title', style: 'margin-top:14px' },
      'Výchozí hodnoty parametrů',
      infoDot(store, 'Hodnoty přepsané tímto profilem. Tlačítkem ↺ vrátíte parametr na zděděnou hodnotu.'),
    ),
    paramsBox,
  );

  const dlg = openDialog({
    title: `Profil: ${p0.name}`,
    body,
    wide: true,
    footer: [
      el('button', { className: 'btn', onclick: () => dlg.close() }, 'Zavřít'),
      el(
        'button',
        {
          className: 'btn btn--primary',
          onclick: () => {
            saveMeta();
            dlg.close();
          },
        },
        'Uložit',
      ),
    ],
  });

  function field(label: string, input: HTMLElement): HTMLElement {
    return el('div', {}, el('label', { className: 'label' }, label), input);
  }
}

/** Kompaktní přepínač profilů do hlavičky. */
export function profileSwitcher(store: Store, onChanged: () => void): HTMLElement {
  const wrap = el('div', { className: 'profile-switcher' });
  const render = (): void => {
    clear(wrap);
    const s = store.get();
    const visible = sortedProfiles(s.profiles).filter((p) => p.visible || p.id === s.activeProfileId);
    const active = s.profiles.find((p) => p.id === s.activeProfileId);
    const select = el(
      'select',
      {
        className: 'select select--small profile-switcher__select',
        'aria-label': 'Aktivní profil',
        onchange: (e: Event) => {
          const id = (e.target as HTMLSelectElement).value;
          store.update(applyProfileToSession(store.get(), id));
          onChanged();
          render();
        },
      },
      ...visible.map((p) => el('option', { value: p.id, selected: p.id === s.activeProfileId }, p.name)),
    );
    wrap.append(
      el('span', { className: 'profile-switcher__icon', style: `color:${active?.color ?? 'inherit'}`, 'aria-hidden': 'true' }, iconOrEmoji(active?.icon ?? 'profile', { size: 15 })),
      select,
    );
  };
  render();
  // Překreslit při změnách profilů odjinud (editor, import…).
  store.subscribe(() => render());
  return wrap;
}

/** Pomůcka: je-li hodnota ikonou, vrať ji, jinak výchozí. */
export function safeIcon(value: string, fallback: IconName = 'profile'): IconName {
  return isIconName(value) ? value : fallback;
}
